export type QuoteBuilderEventSummaryInput = {
  eventDate?: Date
  eventLocation?: string
}

export type QuoteBuilderEventsSummary = {
  /** e.g. `2 events`, `1 event`, or an empty string when nothing is attached. */
  countLabel: string
  /** e.g. `Aug 23 – 24, 2026 · Colorado Convention Center`. May be empty. */
  detail: string
  hasEvents: boolean
}

const monthDay = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' })
const monthDayYear = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' })

/**
 * Human date range for the events attached to a quote.
 * Collapses shared month/year so `Aug 23, 2026` + `Aug 24, 2026` reads
 * `Aug 23 – 24, 2026` the way the customer-facing quote does.
 */
export function formatQuoteEventDateRange(dates: Date[]): string {
  const sorted = dates
    .filter((date): date is Date => date instanceof Date && !Number.isNaN(date.getTime()))
    .slice()
    .sort((a, b) => a.getTime() - b.getTime())

  if (sorted.length === 0) return ''

  const first = sorted[0]
  const last = sorted[sorted.length - 1]
  if (first.getTime() === last.getTime()) return monthDayYear.format(first)

  if (first.getFullYear() !== last.getFullYear()) {
    return `${monthDayYear.format(first)} – ${monthDayYear.format(last)}`
  }
  if (first.getMonth() !== last.getMonth()) {
    return `${monthDay.format(first)} – ${monthDayYear.format(last)}`
  }
  return `${monthDay.format(first)} – ${last.getDate()}, ${last.getFullYear()}`
}

export function summarizeQuoteBuilderEvents(
  events: QuoteBuilderEventSummaryInput[],
): QuoteBuilderEventsSummary {
  if (events.length === 0) {
    return { countLabel: '', detail: '', hasEvents: false }
  }

  const dateRange = formatQuoteEventDateRange(
    events.map((event) => event.eventDate).filter((date): date is Date => !!date),
  )
  const location = events.map((event) => event.eventLocation?.trim()).find((value) => !!value) ?? ''

  return {
    countLabel: `${events.length} ${events.length === 1 ? 'event' : 'events'}`,
    detail: [dateRange, location].filter(Boolean).join(' · '),
    hasEvents: true,
  }
}
